import React, { useState, useContext, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import api from '../api/axios';
import { toast } from 'react-toastify';
import {
    BriefcaseIcon,
    EnvelopeIcon,
    LockClosedIcon,
    ArrowRightIcon,
    ChartBarSquareIcon,
    UserGroupIcon,
    ShieldCheckIcon,
    ExclamationTriangleIcon,
    InformationCircleIcon,
} from '@heroicons/react/24/outline';

// ✅ Import version from package.json
import packageJson from '../../package.json';
// --- CONFIG ---
const APP_VERSION = `v${packageJson.version}`;

// --- UI COMPONENT: Logo ---
const Logo = ({ className, textClass = "text-gray-900", iconClass = "text-indigo-600 bg-indigo-50" }) => (
    <div className={`flex items-center space-x-3 ${className}`}>
        <div className={`p-2.5 rounded-xl ${iconClass}`}>
            <BriefcaseIcon className="h-6 w-6 sm:h-8 sm:w-8" />
        </div>
        <span className={`text-xl sm:text-2xl font-black tracking-tight ${textClass}`}>SmartBreak</span>
    </div>
);

// --- UI COMPONENT: Left Side Feature Row ---
const FeatureRow = ({ icon: Icon, title, description }) => (
    <div className="flex items-start space-x-4 p-4 rounded-2xl bg-white/5 border border-white/10 hover:bg-white/10 transition-colors backdrop-blur-sm">
        <div className="flex-shrink-0">
            <div className="p-3 bg-indigo-500/20 rounded-xl border border-indigo-500/20">
                <Icon className="h-6 w-6 text-indigo-300" />
            </div>
        </div>
        <div>
            <h3 className="text-white font-bold text-lg">{title}</h3>
            <p className="text-indigo-200/70 text-sm mt-1 leading-relaxed">{description}</p>
        </div>
    </div>
);

const Login = () => {
    const { login } = useContext(AuthContext);
    const navigate = useNavigate();

    const [rememberMe, setRememberMe] = useState(false);
    const [formData, setFormData] = useState({ email: '', password: '' });
    const [isLoading, setIsLoading] = useState(false);
    const [savedEmails, setSavedEmails] = useState([]);

    const [errorMessage, setErrorMessage] = useState('');
    const [infoMessage, setInfoMessage] = useState('');

    // On load: restore saved emails and show session expiry reason if redirected
    useEffect(() => {
        const storedEmails = JSON.parse(localStorage.getItem('saved_emails') || '[]');
        setSavedEmails(storedEmails);
        if (storedEmails.length > 0) {
            setFormData(prev => ({ ...prev, email: storedEmails[0] }));
            setRememberMe(true);
        }

        // Show reason if redirected from axios interceptor (e.g. session expired / account inactive)
        const params = new URLSearchParams(window.location.search);
        const reason = params.get('reason');
        if (reason) {
            setInfoMessage(decodeURIComponent(reason));
            window.history.replaceState({}, '', '/');
        }
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setErrorMessage('');
        setInfoMessage('');
        setIsLoading(true);
        try {
            const res = await api.post('/auth/login', formData);

            if (rememberMe) {
                let currentList = JSON.parse(localStorage.getItem('saved_emails') || '[]');
                currentList = currentList.filter(e => e !== formData.email);
                currentList.unshift(formData.email);
                if (currentList.length > 5) currentList.pop();
                localStorage.setItem('saved_emails', JSON.stringify(currentList));
            }

            login(res.data.token, res.data.user, rememberMe);
            navigate('/dashboard');
        } catch (err) {
            const status = err.response?.status;
            const message = err.response?.data?.error || "Login Failed";

            setErrorMessage(message);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex bg-white font-sans">

            {/* --- LEFT SIDE: Feature Showcase (Hidden on Mobile) --- */}
            <div className="hidden lg:flex lg:w-1/2 xl:w-7/12 relative bg-slate-900 overflow-hidden">
                {/* Background Decoration */}
                <div className="absolute inset-0 bg-gradient-to-br from-indigo-900 via-slate-900 to-black z-0"></div>
                <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-indigo-600/20 rounded-full blur-[120px] -translate-y-1/2 translate-x-1/3"></div>
                <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-purple-600/10 rounded-full blur-[100px] translate-y-1/3 -translate-x-1/3"></div>

                {/* Content Container */}
                <div className="relative z-10 w-full h-full flex flex-col justify-between p-16 xl:p-20">
                    <div>
                        <Logo textClass="text-white" iconClass="bg-white/10 text-white" />
                        <div className="mt-12 max-w-lg">
                            <h1 className="text-4xl xl:text-5xl font-extrabold text-white tracking-tight leading-tight mb-6">
                                Streamlines-s yourS <br />
                                <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 to-purple-400">
                                    HR WorkflowS.
                                </span>
                            </h1>
                            <p className="text-lg text-slate-400 mb-10">
                                Experience the next generation of employee management. Simple, fast, and intelligent.
                            </p>

                            {/* Feature List */}
                            <div className="space-y-4">
                                <FeatureRow
                                    icon={ChartBarSquareIcon}
                                    title="Real-time Analytics"
                                    description="Gain insights into leave trends and team availability instantly."
                                />
                                <FeatureRow
                                    icon={UserGroupIcon}
                                    title="Team Management"
                                    description="Effortlessly manage roles, approvals, and employee profiles."
                                />
                                <FeatureRow
                                    icon={ShieldCheckIcon}
                                    title="Enterprise Security"
                                    description="Your data is protected with industry-standard encryption."
                                />
                            </div>
                        </div>
                    </div>

                    {/* Footer with Version */}
                    <div className="flex items-center justify-between text-sm text-slate-500 border-t border-white/10 pt-6">
                        <span>&copy; {new Date().getFullYear()} SmartBreak. All rights reserved.</span>
                        <span className="px-2 py-0.5 rounded-md bg-white/5 border border-white/5 font-mono text-xs text-indigo-300/80">
                            {APP_VERSION}
                        </span>
                    </div>
                </div>
            </div>

            {/* --- RIGHT SIDE: Login Form --- */}
            <div className="w-full lg:w-1/2 xl:w-5/12 flex items-center justify-center p-6 sm:p-12 xl:p-16 bg-white relative">
                <div className="w-full max-w-[420px] space-y-8">

                    {/* Mobile Logo */}
                    <div className="lg:hidden flex justify-center mb-6">
                        <Logo />
                    </div>

                    <div className="text-center lg:text-left">
                        <h2 className="text-3xl font-bold text-slate-900 tracking-tight">Welcome Back</h2>
                        <p className="mt-2 text-slate-500">Enter your credentials to access your dashboard.</p>
                    </div>

                    {/* Inline error banner */}
                    {errorMessage && (
                        <div className="flex items-start gap-3 p-4 rounded-xl bg-rose-50 border border-rose-200 text-rose-700">
                            <ExclamationTriangleIcon className="h-5 w-5 flex-shrink-0 mt-0.5" />
                            <p className="text-sm font-medium">{errorMessage}</p>
                        </div>
                    )}

                    {/* Inline info banner (session expired / account inactive redirect) */}
                    {infoMessage && !errorMessage && (
                        <div className="flex items-start gap-3 p-4 rounded-xl bg-amber-50 border border-amber-200 text-amber-700">
                            <InformationCircleIcon className="h-5 w-5 flex-shrink-0 mt-0.5" />
                            <p className="text-sm font-medium">{infoMessage}</p>
                        </div>
                    )}

                    <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
                        <div className="space-y-5">

                            {/* Email Input */}
                            <div>
                                <label className="block text-sm font-bold text-slate-700 mb-1.5 ml-1">Email Address</label>
                                <div className="relative group">
                                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                                        <EnvelopeIcon className="h-5 w-5 text-slate-400 group-focus-within:text-indigo-500 transition-colors" />
                                    </div>
                                    <input
                                        type="email"
                                        required
                                        list="email-history"
                                        className="block w-full pl-11 pr-4 py-3.5 border border-slate-200 rounded-xl bg-slate-50 text-slate-900 placeholder-slate-400 focus:outline-none focus:bg-white focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all font-medium"
                                        placeholder="name@company.com"
                                        value={formData.email}
                                        onChange={e => setFormData({ ...formData, email: e.target.value })}
                                        autoComplete="email"
                                    />
                                    <datalist id="email-history">
                                        {savedEmails.map((email, index) => (
                                            <option key={index} value={email} />
                                        ))}
                                    </datalist>
                                </div>
                            </div>

                            {/* Password Input */}
                            <div>
                                <label className="block text-sm font-bold text-slate-700 mb-1.5 ml-1">Password</label>
                                <div className="relative group">
                                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                                        <LockClosedIcon className="h-5 w-5 text-slate-400 group-focus-within:text-indigo-500 transition-colors" />
                                    </div>
                                    <input
                                        type="password"
                                        required
                                        className="block w-full pl-11 pr-4 py-3.5 border border-slate-200 rounded-xl bg-slate-50 text-slate-900 placeholder-slate-400 focus:outline-none focus:bg-white focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all font-medium"
                                        placeholder="••••••••"
                                        value={formData.password}
                                        onChange={e => setFormData({ ...formData, password: e.target.value })}
                                        autoComplete="current-password"
                                    />
                                </div>
                            </div>

                            {/* Options */}
                            <div className="flex items-center justify-between pt-1">
                                <div className="flex items-center">
                                    <input
                                        id="remember-me"
                                        name="remember-me"
                                        type="checkbox"
                                        className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded cursor-pointer"
                                        checked={rememberMe}
                                        onChange={(e) => setRememberMe(e.target.checked)}
                                    />
                                    <label htmlFor="remember-me" className="ml-2 block text-sm font-medium text-slate-600 cursor-pointer select-none">Remember me</label>
                                </div>
                                <div className="text-sm">
                                    <Link to="/forgot-password" className="font-bold text-indigo-600 hover:text-indigo-500 hover:underline">
                                        Forgot password?
                                    </Link>
                                </div>
                            </div>
                        </div>

                        {/* Submit Button */}
                        <button
                            type="submit"
                            disabled={isLoading}
                            className={`w-full flex justify-center items-center py-4 px-4 border border-transparent rounded-xl shadow-xl text-sm font-bold text-white transition-all transform
                                bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 shadow-indigo-500/30 hover:-translate-y-0.5
                                ${isLoading ? 'opacity-70 cursor-not-allowed' : ''}
                                focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500`}
                        >
                            {isLoading ? (
                                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                            ) : null}
                            {isLoading ? 'Signing In...' : 'Sign In'}
                            {!isLoading && <ArrowRightIcon className="ml-2 h-4 w-4" />}
                        </button>
                    </form>

                    {/* Mobile Version Indicator (Hidden on Desktop) */}
                    <div className="lg:hidden mt-8 text-center border-t border-slate-100 pt-4">
                        <span className="text-[10px] font-mono text-slate-300">{APP_VERSION}</span>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Login;